import { useTranslation } from 'react-i18next'

import { COUNTRY_CODES } from '@/shared/config/countries'
import { FALLBACK_LOCALE } from '@/shared/i18n'
import type { ComboboxOption } from '@/shared/ui/combobox'

/*
 * Deliberately not re-exported from `@/shared/lib`. That barrel is in the eager
 * initial closure — `cn` alone puts it there — so a re-export hoists the 249
 * codes into the first download for the sake of two lazy routes. Measured at
 * 1.0 kB gzip against a budget with less than that to spare. Import this file
 * by path.
 *
 * Country names come from the browser's own CLDR data through
 * `Intl.DisplayNames`, not from a package and not from our locale bundles.
 * Translating 249 names twice by hand is work nobody would keep correct, and
 * the runtime already ships them in every language the panel speaks.
 *
 * Both caches are module-level and keyed by language: the list is stable for
 * the life of the tab, and building it means 249 lookups plus a collator sort.
 */
const names = new Map<string, Map<string, string>>()
const options = new Map<string, ComboboxOption[]>()

function displayNames(language: string) {
  return new Intl.DisplayNames([language], { type: 'region' })
}

function namesFor(language: string) {
  let cached = names.get(language)
  if (cached === undefined) {
    const display = displayNames(language)
    cached = new Map(COUNTRY_CODES.map((code) => [code, display.of(code) ?? code]))
    names.set(language, cached)
  }
  return cached
}

function optionsFor(language: string) {
  let cached = options.get(language)
  if (cached === undefined) {
    cached = [...namesFor(language)]
      .map(([value, label]) => ({ value, label }))
      // Alphabetical in the language on screen — «Австралия» sorts under А for a
      // Russian reader and under A for an English one, and neither ordering is
      // the other's.
      .sort((a, b) => a.label.localeCompare(b.label, language))
    options.set(language, cached)
  }
  return cached
}

/**
 * The active i18n language, always a string `Intl` will accept — a component
 * can render before i18n has resolved one, and `Intl` throws on `undefined`
 * rather than falling back to anything.
 */
function useLanguage() {
  const { i18n } = useTranslation()
  return i18n.resolvedLanguage ?? i18n.language ?? FALLBACK_LOCALE
}

/** Every country, localized and sorted, ready for `MultiSelect` / `Combobox`. */
export function useCountryOptions(): ComboboxOption[] {
  return optionsFor(useLanguage())
}

/**
 * Code → localized name, for reading back a value the panel did not offer —
 * a breakdown row, a lead's country column.
 *
 * Falls through to `Intl` for codes outside our list (MaxMind's `XK`), and to
 * the code itself for anything it does not recognise, so a row never renders
 * blank.
 */
export function useCountryName(): (code: string) => string {
  const language = useLanguage()
  const known = namesFor(language)

  return (code: string) => {
    const name = known.get(code)
    if (name !== undefined) return name

    try {
      return displayNames(language).of(code) ?? code
    } catch {
      // `of` throws on anything that is not a well-formed region subtag.
      return code
    }
  }
}
